import type { CommerceCapability } from './commerceCapabilities';

export type AdminSurfaceStatus = 'live' | 'partial' | 'planned' | 'blocked' | 'external';

export interface AdminNavItem {
  to: string;
  label: string;
  meta: string;
  icon: string;
  aliases?: string[];
  soon?: boolean;
  write?: boolean;
  /** commerce-rbac-v1 read key; independent from the generic mutation flag. */
  capability?: CommerceCapability;
  /** C7(spec §5):role 過濾後所屬域僅剩此一項時,併入此 label 的域並隱藏原域。 */
  fallbackGroup?: string;
}

export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

export interface AdminMechanism {
  id: string;
  area: string;
  job: string;
  status: AdminSurfaceStatus;
  route?: string;
  contract: string;
  guard: string;
  referenceDomain?: string;
}

export interface PlatformReferenceLens {
  id: string;
  domain: string;
  referenceSurface: string;
  lsCloudDecision: string;
  status: AdminSurfaceStatus;
  route?: string;
  nextStep: string;
}

export interface AdminReportDefinition {
  id: string;
  title: string;
  domain: string;
  question: string;
  status: AdminSurfaceStatus;
  source: string;
  route?: string;
  drilldown: string;
  freshness: string;
  failureSegment: string;
}

export interface PlatformMonitorComponent {
  id: string;
  component: string;
  status: AdminSurfaceStatus;
  owner: string;
  liveSignal: string;
  monitoringSource: string;
  route?: string;
  runbook: string;
  failureSegment: string;
}

export interface FailureSegment {
  id: string;
  label: string;
  owner: string;
  when: string;
  action: string;
}

export interface DashboardWorkflow {
  title: string;
  scope: string;
  control: string;
  tone: 'pill-good' | 'pill-info' | 'pill-warn' | 'pill-neutral';
  to: string;
}

export const STATUS_LABEL: Record<AdminSurfaceStatus, string> = {
  live: '可操作',
  partial: '已接端點',
  planned: '待端點',
  blocked: '待決策',
  external: '外部系統',
};

export const STATUS_TONE: Record<AdminSurfaceStatus, string> = {
  live: 'pill-good',
  partial: 'pill-info',
  planned: 'pill-warn',
  blocked: 'pill-warn',
  external: 'pill-neutral',
};

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    // pinned:域外首頁(業主 2026-07-09 定案「1 首頁 + 4 域」)
    label: '',
    items: [{ to: '/', label: '總覽', meta: '今日工作台', icon: 'OV' }],
  },
  {
    // 高頻:客服/資安從工單出發,對終端帳號與裝置查、判、處置
    label: '客戶支援',
    items: [
      { to: '/users', label: '使用者', meta: '終端帳號全貌', icon: 'US' },
      { to: '/devices', label: '裝置', meta: '機隊瀏覽與處置', icon: 'DV' },
      { to: '/compliance', label: '合規處置', meta: 'DSAR / 帳號刪除代理', icon: 'CP', write: true },
    ],
  },
  {
    // 新域(R19,提案 admin!40):cs-copilot 治理面整入。S23–S27 五頁全接 restful
    // /v3/admin/ai/* 代理為真頁(對話稽核 / 回答門檻 / 轉真人 / 知識缺口 / 管家稽核)。
    // 依 §10.A S23–S27。
    label: 'AI 營運',
    items: [
      { to: '/ai/conversations', label: '對話稽核', meta: 'AI 客服/管家對話下鑽', icon: 'AC' },
      { to: '/ai/guardrail', label: '回答門檻政策', meta: '門檻校調 / 試問', icon: 'GR', write: true },
      { to: '/ai/handoffs', label: '轉真人收件匣', meta: '客服升級單', icon: 'HO', write: true },
      { to: '/ai/kb-gaps', label: '知識缺口', meta: '無解答問句彙集', icon: 'KG' },
      { to: '/ai/butler-audit', label: '管家稽核', meta: '主動卡・相機軌・記憶稽核', icon: 'BA' },
      // S41 AI 使用記錄(各 consumer 的 LLM 呼叫稽核;CanAdminRead 可見、點列下鑽完整內容)
      { to: '/ai-usage', label: 'AI 使用記錄', meta: 'LLM 呼叫稽核與用量', icon: 'UM' },
      // S40 管理助理 / AI 金鑰登錄台(platform-owner 可設 LLM 金鑰;助理浮窗本體為全域,由 AppShell 掛載、不佔路由)
      { to: '/assistant-settings', label: '管理助理', meta: 'AI 金鑰登錄台 / 助理設定', icon: 'AS' },
    ],
  },
  {
    // 中頻:平台上架什麼、發布到哪、賣給誰的生命週期治理
    // (S16 /organizations 依 specs/01 §4 遷入「開發者組織」域)
    label: '產品與生態',
    items: [
      { to: '/dev-reviews', label: '開發者審核', meta: '組織核可 / 機型發布', icon: 'DR', write: true },
      { to: '/customer-families', label: '客戶與品牌', meta: '白牌與用戶端應用掛載', icon: 'CF', write: true },
      { to: '/companies', label: 'B2B 公司', meta: '出貨綁定 / 開票', icon: 'CO', write: true },
      { to: '/firmware', label: '韌體釋出', meta: 'OTA 生命週期', icon: 'FW', write: true },
      // R6 用戶端版本策略:佔導覽位的未開項(ADR-031 依據;spec §10)
      { to: '/client-versions', label: '用戶端版本', meta: '版本策略(R6)', icon: 'CV', write: true, soon: true },
    ],
  },
  {
    // 新域(specs/01 §2.1,admin#3 P2):Issuer 批次匯入→release→分配前 inventory 治理;
    // 不簽發(鐵則 1)。清冊/批次/異常已接 restful 白名單代理 → ca-manager。
    // 依 §3.2 對 cs-readonly 隱藏(write 旗標=platform-owner/admin/security-officer 可見)。
    label: 'AID 供應鏈',
    items: [
      { to: '/aid/inventory', label: 'AID 清冊', meta: '清冊與生命週期', icon: 'AI', write: true },
      { to: '/aid/batches', label: '批次匯入與釋出', meta: '簽發方清單檔匯入與釋出', icon: 'AB', write: true },
      { to: '/aid/anomalies', label: 'AID 異常與稽核', meta: '不一致偵測與池水位', icon: 'AA', write: true },
      { to: '/aid/pos', label: 'PO 配給', meta: 'PO 建立與池配給', icon: 'AP', write: true },
    ],
  },
  {
    // 新域(specs/01 §2.2):developer org read model + PO/AID allocation + 配額用量。
    // S16 自「產品與生態」遷入;cs-readonly 視圖下本域僅剩 S16 → 依 C7 併入客戶支援。
    // S31 AID 分配後端(/v3/admin/aid-allocations*,restful#74)已在 main → live
    // (admin#3 P2);S32 組織配額用量端點待落地 → 維持【即將開放】佔位。
    label: '開發者組織',
    items: [
      { to: '/organizations', label: '開發者組織', meta: '成員與邀請治理', icon: 'OR', fallbackGroup: '客戶支援' },
      { to: '/aid-allocations', label: 'AID 分配', meta: 'PO 分配與配額上限', icon: 'AL', write: true },
      { to: '/org-usage', label: '組織配額與用量', meta: '工作區與模擬器用量', icon: 'OU', write: true, soon: true },
    ],
  },
  {
    // 新域(specs/01 §2.3,P1=admin#3):Plan catalog 版本治理、Customer entitlement/grant、
    // Accounting ledger 與 gateway reconciliation。S35/S36 依 §3.2 帳務讀取排除 cs-readonly
    // (write 旗標);S33/S34 檢視含 cs-readonly(支援解釋用)。
    label: '方案與帳務',
    items: [
      { to: '/plans', label: '方案目錄', meta: '方案版本與發布治理', icon: 'PL', capability: 'plan-catalog:read' },
      { to: '/plan-grants', label: '權益贈與', meta: '來源鏈與贈與治理', icon: 'PG', capability: 'plan-grant:read' },
      // capability 是刻意加的,不是漏了:這一頁是跨租戶的商業 read model
      // (全平台每個 org 的席次/裝置/配額)。少了它,nav 對 cs-readonly 與 ai-ops
      // 都可見,而伺服端當時的 requireAdminRead 也放行 —— support-only 角色因此
      // 能盤點整個客戶base。D31② need-to-know(NIST SP 800-53 AC-6);伺服端已於
      // restful!391 收斂為 commerce read 述詞,兩側必須同時收窄才有意義。
      { to: '/ops-governance', label: 'Ops 租戶治理', meta: '組織開通・配額・用量', icon: 'OG', capability: 'commerce:read' },
      { to: '/accounting', label: '帳務總帳', meta: '總帳・退款・調帳', icon: 'LG', capability: 'commerce:read' },
      { to: '/reconciliations', label: '對帳工作流', meta: '金流方差異與結案', icon: 'RC', capability: 'commerce:read' },
    ],
  },
  {
    // 值班:偵測 → 定位 → 覆盤;處置動作留在實體頁
    label: '維運觀測',
    items: [
      { to: '/monitoring', label: '平台監控', meta: '元件健康 / 故障歸因', icon: 'MO' },
      { to: '/reports', label: '分析報表', meta: '維運 / 治理報表目錄', icon: 'RP' },
      { to: '/diagnostics', label: '診斷鎖定', meta: '觀測範圍 / 範圍化紀錄', icon: 'DG' },
    ],
  },
  {
    // 低頻高風險:credential、權限、稽核與平台組態(業主定名 2026-07-09)
    // specs/01 §2.4 擴充:S37 生態系治理已落地(admin#6,restful#75);S38/S39 治理面待後端 → 【即將開放】佔位
    label: '信任與治理',
    items: [
      { to: '/audit', label: '稽核紀錄', meta: '處置軌跡查詢', icon: 'AU' },
      { to: '/certificates', label: '憑證 / PKI', meta: '裝置信任簽發', icon: 'PK', write: true },
      { to: '/api-keys', label: 'API 金鑰', meta: '夥伴金鑰 / 撤銷', icon: 'AK', write: true },
      { to: '/team', label: '維運團隊', meta: '維運者 / 角色授權', icon: 'RB', write: true, aliases: ['/roles'] },
      { to: '/platform-settings', label: '平台設定', meta: '設定投影(唯讀)', icon: 'PS' },
      { to: '/operations', label: '能力覆蓋', meta: '機制盤點 / 升級紀律', icon: 'OP' },
      { to: '/ecosystems', label: '生態系治理', meta: '審核・清冊・停權', icon: 'EC', write: true },
      { to: '/helpdesk-governance', label: '維修與客服治理', meta: '維運者授權・政策・稽核', icon: 'HG', write: true },
      { to: '/debug-governance', label: '除錯治理', meta: '除錯工作階段摘要', icon: 'DB', write: true, soon: true },
    ],
  },
];

const LIVE: AdminMechanism[] = [
  {
    id: 'dashboard',
    area: '總覽 KPI',
    job: '使用者、裝置、在線、事件、OTA 任務快照',
    status: 'live',
    route: '/',
    contract: 'GET /v3/admin/stats + /v3/version',
    guard: '唯讀；stats 失敗不阻斷主控台',
    referenceDomain: 'Data Dashboard',
  },
  {
    id: 'users-browse',
    area: '使用者瀏覽',
    job: '依 region/status 分頁、email 精準查詢',
    status: 'live',
    route: '/users',
    contract: 'GET /v3/admin/users',
    guard: 'opaque cursor；403 顯示 request_id',
    referenceDomain: 'User Account',
  },
  {
    id: 'customer-360',
    area: '終端帳號全貌',
    job: '個人檔案、裝置、訂閱、近期事件、帳號處置',
    status: 'live',
    route: '/users',
    contract: 'GET /v3/admin/users/{id} + suspend/ban/force-logout/impersonate',
    guard: '敏感操作確認；case_id/reason；impersonate 必填 case_id',
    referenceDomain: 'Home / User / Device scope',
  },
  {
    id: 'devices',
    area: '裝置管理',
    job: '裝置搜尋、詳情、停用/啟用/掛失',
    status: 'live',
    route: '/devices',
    contract: 'GET /v3/admin/devices + device lifecycle actions',
    guard: '敏感操作確認；reason/case_id；伺服端 audit',
    referenceDomain: 'Device Management',
  },
  {
    id: 'device-devlog',
    area: '裝置除錯記錄',
    job: '對單台裝置啟用 15 分鐘 devlog SSE 串流',
    status: 'live',
    route: '/devices',
    contract: 'POST/DELETE /v3/admin/devices/{id}/devlog + stream',
    guard: 'RBAC + audit；離頁自動停用；本機 cap 500 行',
    referenceDomain: 'Device Log Query',
  },
  {
    id: 'platform-diagnostics',
    area: 'Device/App/平台診斷',
    job: '跨 device、app、event-system、signaling、recording、firmware、infra component 定位問題',
    status: 'partial',
    route: '/monitoring',
    contract: '/v3/admin/devices + audit/devlog + external observability links',
    guard: 'Admin 可全域檢視；Developer Portal 僅能查自己 org 的 device/app scope',
    referenceDomain: 'Cloud Monitoring / Device Debug',
  },
  {
    id: 'diagnostic-locks',
    area: '診斷鎖定',
    job: '鎖定 user/device 後續 log scope，供 Loki/Tempo 依 diagnostic_scope_id 追查',
    status: 'live',
    route: '/diagnostics',
    contract: 'GET/POST/DELETE /v3/admin/diagnostic-locks',
    guard: 'reason/case_id 必填；TTL 5m~24h；hot path fail-open；raw ID 不作觀測查詢鍵',
    referenceDomain: 'Observability / Scoped logging',
  },
  {
    id: 'analytics-reports',
    area: '分析報表',
    job: '業務、機隊、韌體、開發者、安全、合規、平台用量報表目錄與即時 KPI',
    status: 'partial',
    route: '/reports',
    contract: 'GET /v3/admin/stats + reporting catalog SSOT',
    guard: '已接資料源才標 live；aggregation API 缺口必須顯示',
    referenceDomain: 'Data Analytics / Data Dashboard',
  },
  {
    id: 'audit',
    area: '稽核紀錄',
    job: '依 對象 / 操作者 / 案件查詢管理行為',
    status: 'live',
    route: '/audit',
    contract: 'GET /v3/admin/audit-logs',
    guard: '三擇一查詢軸；顯示 request_id',
    referenceDomain: 'Cloud Monitoring',
  },
  {
    id: 'developer-review',
    area: '開發者審核',
    job: '組織開通核可與機型發布審核（個人開發者自助開通、免審核）',
    status: 'live',
    route: '/dev-reviews',
    contract: 'GET/POST /v3/admin/dev/*',
    guard: '駁回理由必填；伺服端 audit',
    referenceDomain: 'Product Development',
  },
  {
    id: 'firmware',
    area: '韌體釋出',
    job: '釋出啟用、暫停、恢復、回滾；image / targeting 檢視',
    status: 'live',
    route: '/firmware',
    contract: 'GET/POST /v3/admin/fw/*',
    guard: '二段確認；狀態機由伺服端裁決',
    referenceDomain: 'OTA Firmware Updates',
  },
  {
    id: 'api-keys',
    area: 'API 金鑰',
    job: '依夥伴查詢、簽發、撤銷',
    status: 'live',
    route: '/api-keys',
    contract: 'GET/POST/DELETE /v3/admin/api-keys',
    guard: '明文只顯示一次；撤銷確認；partner 必填',
    referenceDomain: 'OpenAPI / Partner API',
  },
  {
    id: 'pki',
    area: '憑證 / PKI',
    job: 'device-CA 單台簽發、CA chain、憑證清單',
    status: 'partial',
    route: '/certificates',
    contract: 'GET/POST /v3/admin/pki/*',
    guard: '私鑰不進瀏覽器；device-CA 未配置時誠實顯示未設定',
    referenceDomain: 'Device Trust',
  },
  {
    id: 'roles',
    area: '角色授權',
    job: '維運團隊頁列 maintainer 帳號；platform-owner 授予 user、developer、CS 唯讀與 admin roles（與 /users 終端帳號維運分離）',
    status: 'live',
    route: '/team',
    contract: 'GET /v3/admin/users?role=…；POST /v3/admin/users/{id}/grant-role',
    guard: '僅 platform-owner 可寫；reason/case_id 必填；伺服端 audit',
    referenceDomain: 'Platform IAM',
  },
  {
    id: 'compliance',
    area: '帳號刪除 / DSAR',
    job: '代理刪除、取消、匯出、下載連結查詢',
    status: 'live',
    route: '/compliance',
    contract: '/v3/admin/users/{id}/deletion-* / export/*',
    guard: '寫入需 full admin role + reason/case_id；read 可供支援查詢',
    referenceDomain: 'Account Operations',
  },
  {
    id: 'organizations',
    area: '開發者組織',
    job: '開發者組織資料、擁有者、成員、待處理邀請',
    status: 'live',
    route: '/organizations',
    contract: 'GET /v3/admin/dev/orgs*',
    guard: '讀 + production-grant / 成員治理兩組寫入(S16)；不得顯示 invite token/token_hash',
    referenceDomain: 'Developer Organization',
  },
  {
    id: 'companies',
    area: 'B2B 公司與出貨計費',
    job: '公司名錄、裝置批次、公司發票',
    status: 'live',
    route: '/companies',
    contract: 'GET/POST /v3/admin/companies + /v3/admin/batches',
    guard: '批次綁定裝置與 invoice 同批寫入；void 需 reason/case_id',
    referenceDomain: 'B2B Asset / Billing',
  },
  {
    id: 'platform-settings',
    area: '平台基本資料',
    job: '平台識別、網域、cookie、種子資料、bucket / secret 參照、驗證',
    status: 'live',
    route: '/platform-settings',
    contract: 'GET /v3/admin/platform/settings',
    guard: 'read-only；sanitized env projection；secret 不進瀏覽器',
    referenceDomain: 'Platform Configuration',
  },
  {
    id: 'customer-family',
    area: '客戶 / 品牌(Family) 管理',
    job: '已核可客戶組織、平台 / 自有家族、客戶端 App 種子、遷移預檢',
    status: 'live',
    route: '/customer-families',
    contract: 'GET/POST /v3/admin/customer-families*',
    guard: 'v1 以 approved developer org 作為 customer_ref；只收 app_key_hash；既有 client app family change 僅 preflight',
    referenceDomain: 'Customer / Brand ecosystem',
  },
  {
    id: 'plan-catalog',
    area: '方案目錄',
    job: 'Plan 版本清單/diff、草稿建立、publish/retire;published 版唯讀鎖定',
    status: 'live',
    route: '/plans',
    contract: 'GET/POST /v3/admin/plans + POST/GET /v3/admin/commerce-operations*',
    guard: 'dual-control:reason/case_id 必填;publish/retire 只建立 24h 操作;不同 admin/security-officer/platform-owner 密碼 step-up 後執行',
    referenceDomain: 'Commerce / Plan catalog',
  },
  {
    id: 'plan-grants',
    area: '權益贈與',
    job: '依對象查權益來源鏈、建立限期/永久 grant、撤銷(反向分錄沖銷)',
    status: 'live',
    route: '/plan-grants',
    contract: 'GET /v3/admin/plan-grants + /v3/admin/devices/{id}/entitlements + commerce-operations*',
    guard: 'reason/case_id 必填;只授 published 版本;create/revoke 一律 dual-control;device 顯示 server-authoritative AID/runtime/policy explain',
    referenceDomain: 'Commerce / Entitlement',
  },
  {
    id: 'accounting-ledger',
    area: '帳務總帳',
    job: 'ledger 帳戶/交易 drill-down + 受控 credit/refund/adjustment(append-only)',
    status: 'live',
    route: '/accounting',
    contract: 'GET /v3/admin/accounting/ledger* + POST/GET /v3/admin/commerce-operations*',
    guard: '不 UPDATE/DELETE,更正=反向分錄;payment secret 不進瀏覽器;credit/refund/adjustment 一律 dual-control;讀取排除 cs-readonly',
    referenceDomain: 'Operation Management / Billing',
  },
  {
    id: 'reconciliation',
    area: '對帳工作流',
    job: 'gateway 對帳 run 匯入(冪等)、差異清單、assign/adjust/resolve 結案',
    status: 'live',
    route: '/reconciliations',
    contract: 'POST /v3/admin/reconciliations + {run}/items + assign|resolve',
    guard: '結案必附 reason/case;adjusted 必附 adjustment tx;run 清單端點待落地(UI 以 run_id 查詢,不造假清單)',
    referenceDomain: 'Billing Gateway / Reconciliation',
  },
  {
    id: 'aid-allocation',
    area: 'AID 分配',
    job: '依 PO 把已 release 的 AID inventory 分配給 developer workspace;建立 / 調整計數 / 撤銷 headroom',
    status: 'live',
    route: '/aid-allocations',
    contract: 'GET/POST /v3/admin/aid-allocations + PATCH {id} + POST {id}/revoke',
    guard: 'reason/case_id 必填;超 PO / scope / batch 未 release 後端 fail closed(422);寫入=admin/platform-owner(security-officer 唯讀);撤銷非 dual-control(二段確認)',
    referenceDomain: 'AID Supply Chain / Allocation',
  },
  {
    id: 'aid-inventory',
    area: 'AID 清冊 / 生命週期(S28)',
    job: 'inventory 瀏覽 / 下鑽 + device-ca activation/RMA lineage + AID revoke；不顯示 private key',
    status: 'live',
    route: '/aid/inventory',
    contract: 'GET /v3/admin/aids* + GET /v3/admin/pki/*/aid|lineage + POST /v3/admin/commerce-operations(action=aid.revoke)',
    guard: 'Admin 不簽發；revoke 一律 dual-control，reason/case、發起≠核准、checker 密碼 step-up；device-ca key 留在 REST server side',
    referenceDomain: 'AID Supply Chain',
  },
  {
    id: 'cs-readonly',
    area: 'CS 唯讀角色分流',
    job: '客服唯讀查詢與完整 admin 處置權分離',
    status: 'live',
    route: '/users',
    contract: 'role=cs-readonly + read-only nav/action guard',
    guard: '伺服端對讀寫 endpoint 分流；UI 收起處置入口',
    referenceDomain: 'Support Operations',
  },
  {
    id: 'aid-batches',
    area: '批次匯入與釋出(S29)',
    job: '匯入 Issuer issuance batch manifest + release(批次層動詞=release,不得寫 activate)',
    status: 'live',
    route: '/aid/batches',
    contract: 'GET/POST /v3/admin/imports、/v3/admin/stock(扁平命名,S28 同批)',
    guard: 'Admin 只匯入 / release,不簽發批次;X-Acting-User 轉發',
    referenceDomain: 'AID Supply Chain',
  },
  {
    id: 'aid-anomalies',
    area: 'AID 異常與稽核(S30)',
    job: 'mismatch(shadow/backfill)、debug AID 池水位/到期監控、AID 相關 audit 彙整入口',
    status: 'live',
    route: '/aid/anomalies',
    contract: 'GET /v3/admin/anomalies + 既有 /v3/admin/audit-logs',
    guard: '異常監控為治理稽核面,不觸發簽發/綁定;debug 池權威在 device-ca',
    referenceDomain: 'AID Supply Chain',
  },
];

const ROADMAP: AdminMechanism[] = [
  {
    id: 'spaces-groups-scenes',
    area: '空間 / 群組 / 場景治理',
    job: 'LS automation 需要時才補 home/site/room/group graph、rule health、automation disabled reason',
    status: 'planned',
    contract: 'admin space/group/scene read model + automation admin API',
    guard: '使用者自助仍在 App；admin 不做外部平台式 space/group CRUD',
    referenceDomain: 'Automation / Space graph',
  },
  {
    id: 'message-service',
    area: '訊息服務與事件通道',
    job: '事件通道健康、規則過濾、測試通道、累積告警',
    status: 'planned',
    contract: 'event-system/admin message-channel API',
    guard: '先測試環境後正式；告警收件人不得 hardcode',
    referenceDomain: 'Event Channel / Messaging',
  },
  {
    id: 'quota-usage',
    area: '配額與用量',
    job: '跨用戶 quota 檢視、調額、quota_state 徽章',
    status: 'blocked',
    contract: 'admin quota.read / subscription.quota_adjust',
    guard: '待 ADR-001/025 與管理端點',
    referenceDomain: 'Data Services / Metering',
  },
  {
    id: 'client-app-version',
    area: '客戶端 App 版本策略',
    job: 'min_supported_version、force_upgrade_below、deprecate/revoke',
    status: 'blocked',
    contract: 'client_apps admin API',
    guard: '待 ADR-031 與端點定義',
    referenceDomain: 'App Development',
  },
  {
    id: 'share-graph',
    area: '分享關係處置',
    job: 'owner/shared/pending 圖譜、CS 強制撤銷、擁有權轉移 audit',
    status: 'blocked',
    contract: 'admin share graph/revoke/transfer API',
    guard: '待 APP/18 G-1/G-2 與管理端點',
    referenceDomain: 'Device Sharing',
  },
  {
    id: 'firmware-p2',
    area: '韌體 P2',
    job: 'image binary 上傳、manifest 匯出發佈、ota_jobs 細粒度進度',
    status: 'blocked',
    contract: 'firmware-server FW-1/FW-2',
    guard: '待 manifest 簽章與 DDB x manifest 整合',
    referenceDomain: 'Firmware Management',
  },
];

const EXTERNAL: AdminMechanism[] = [
  {
    id: 'infra-monitoring',
    area: '基礎設施監控與告警',
    job: 'TURN、Event DLQ、Recording、S3 上傳與容量水位',
    status: 'external',
    contract: 'Grafana / CloudWatch / observability baseline',
    guard: '純觀測不進管理主控台',
    referenceDomain: 'Observability',
  },
  {
    id: 'factory-provisioning',
    area: '工廠佈建',
    job: '裝置預註冊、CSR-on-device、passphrase 首發',
    status: 'external',
    contract: 'Factory tool + pki-signer',
    guard: '生產線流程；不暴露一般 admin UI',
    referenceDomain: 'Manufacturing',
  },
  {
    id: 'secret-rotation',
    area: '祕密與金鑰輪替',
    job: 'TURN secret、server_secret、TLS/JWT/OTA 私鑰',
    status: 'external',
    contract: 'Secrets Manager / KMS / infra runbook',
    guard: '私鑰與 secret 不進瀏覽器',
    referenceDomain: 'Platform Security',
  },
  {
    id: 'internal-services',
    area: '服務間 /internal/*',
    job: 'state-write、kick、ota-notify 等機器對機器呼叫',
    status: 'external',
    contract: 'mTLS service-to-service',
    guard: '無人類介面；dev-only bridge 不作正式機制',
    referenceDomain: 'Service Mesh',
  },
  {
    id: 'payment-gateway',
    area: '金流閘道本體',
    job: '卡務、拒付/爭議、dunning 設定',
    status: 'external',
    contract: 'Stripe / gateway dashboard',
    guard: '本主控台只做平台側鏡像與退款處置',
    referenceDomain: 'Billing Gateway',
  },
];

export const ADMIN_MECHANISMS = {
  live: LIVE,
  roadmap: ROADMAP,
  external: EXTERNAL,
} as const;

export const ADMIN_REPORT_CATALOG: AdminReportDefinition[] = [
  {
    id: 'platform-kpi',
    title: '平台 KPI 報表',
    domain: '平台維運',
    question: '平台目前有多少 users、devices、online devices、events 與 OTA jobs。',
    status: 'live',
    source: 'GET /v3/admin/stats',
    route: '/',
    drilldown: '總覽、使用者、裝置、韌體釋出',
    freshness: '請求當下快照;window_hours 由後端回傳',
    failureSegment: 'backend-api',
  },
  {
    id: 'customer-support',
    title: '客服支援報表',
    domain: '客服 / 終端帳號全貌',
    question: '客服處理帳號、裝置、訂閱與近期事件時需要哪些可追溯資料。',
    status: 'partial',
    source: 'GET /v3/admin/users + /v3/admin/users/{id} + audit logs',
    route: '/users',
    drilldown: '使用者瀏覽、終端帳號全貌、稽核',
    freshness: '每位使用者即時明細;尚無跨時間窗彙總',
    failureSegment: 'data-seed',
  },
  {
    id: 'fleet-health',
    title: '機隊健康報表',
    domain: '裝置 / 機隊',
    question: '裝置總量、online/offline、停用/掛失、devlog readiness 與 model 分布。',
    status: 'partial',
    source: 'GET /v3/admin/devices + /v3/admin/stats + device devlog',
    route: '/devices',
    drilldown: '裝置瀏覽、裝置詳情、devlog 面板',
    freshness: '即時瀏覽;機型 / 狀態趨勢仍待 reporting aggregation API',
    failureSegment: 'backend-api',
  },
  {
    id: 'firmware-ota',
    title: '韌體 / OTA 報表',
    domain: '產品 / 韌體',
    question: '韌體 release 狀態、canary/rollback、OTA success/fail 與 image ownership。',
    status: 'partial',
    source: 'GET /v3/admin/fw/releases + /images + /targeting',
    route: '/firmware',
    drilldown: '韌體生命週期表',
    freshness: '即時生命週期狀態;ota_jobs 細粒度進度待 FW-1/FW-2',
    failureSegment: 'backend-api',
  },
  {
    id: 'developer-org',
    title: '開發者 / 組織報表',
    domain: '開發者生態',
    question: '開發者申請、org owner/member、pending invites、model review backlog。',
    status: 'partial',
    source: 'GET /v3/admin/dev/applications + /reviews + /dev/orgs',
    route: '/dev-reviews',
    drilldown: '開發者審核、開發者組織',
    freshness: '即時佇列;組織成長 / 採用趨勢待 aggregation API',
    failureSegment: 'testcase-fixture',
  },
  {
    id: 'security-rbac',
    title: '安全 / RBAC 報表',
    domain: '信任 / IAM',
    question: 'admin/security-officer/platform-owner 分布、API key scope、PKI readiness 與高風險操作。',
    status: 'partial',
    source: 'Roles UI + API keys + PKI + audit logs',
    route: '/audit',
    drilldown: '稽核、角色、API 金鑰、PKI',
    freshness: '即時讀取路徑;角色清單彙總待 endpoint',
    failureSegment: 'spec-contract',
  },
  {
    id: 'compliance-dsar',
    title: '合規 / DSAR 報表',
    domain: '合規',
    question: '帳號刪除、匯出、代理處置與稽核查詢是否可追蹤。',
    status: 'partial',
    source: 'Compliance endpoints + audit logs',
    route: '/compliance',
    drilldown: '合規處置、稽核',
    freshness: '即時個別主體處置;SLA aging 報表待 aggregation API',
    failureSegment: 'backend-api',
  },
  {
    id: 'b2b-billing',
    title: 'B2B 帳務報表',
    domain: '商業維運',
    question: 'company、device batch、invoice 與 void 狀態是否能支援出貨/帳務對帳。',
    status: 'partial',
    source: 'GET/POST /v3/admin/companies + /batches + /invoices',
    route: '/companies',
    drilldown: 'B2B 公司',
    freshness: '即時公司 / 批次清單;金流閘道對帳待 ADR / endpoint',
    failureSegment: 'data-seed',
  },
  {
    id: 'platform-usage',
    title: '平台用量報表',
    domain: '用量 / 計量',
    question: 'API calls、message volume、storage、recording、quota 與 customer/family 用量。',
    status: 'planned',
    source: 'reporting aggregation API + observability pipeline',
    route: '/monitoring',
    drilldown: '平台監控、機制矩陣',
    freshness: 'admin 尚未提供',
    failureSegment: 'backend-api',
  },
];

export const PLATFORM_MONITOR_COMPONENTS: PlatformMonitorComponent[] = [
  {
    id: 'rest-api',
    component: 'REST API',
    status: 'live',
    owner: '後端 / Scott',
    liveSignal: 'GET /v3/version、GET /v3/admin/stats',
    monitoringSource: 'Admin 即時探測 + 服務 logs',
    route: '/platform-settings',
    runbook: '確認 deploy version、CORS、RBAC、request_id 與 API edge routing。',
    failureSegment: 'backend-api',
  },
  {
    id: 'admin-console',
    component: 'Admin Console',
    status: 'live',
    owner: '平台管理網站',
    liveSignal: 'SPA 路由渲染 + E2E console 檢查',
    monitoringSource: 'Playwright、Caddy access/error logs',
    route: '/',
    runbook: '確認 dist deploy、Caddy root、Vite base、client id/key 與 browser console。',
    failureSegment: 'frontend-code',
  },
  {
    id: 'developer-portal',
    component: 'Developer Portal',
    status: 'partial',
    owner: '開發者體驗',
    liveSignal: '僅限 org-scoped `/v3/dev/*`',
    monitoringSource: 'Portal E2E + REST org membership 守門',
    route: '/organizations',
    runbook: '確認 X-Org-Id、org_members 伺服端重查、不可跨 org。',
    failureSegment: 'spec-contract',
  },
  {
    id: 'web-app',
    component: 'User Web App',
    status: 'live',
    owner: '客戶端 App',
    liveSignal: 'v3-webapp-dev 根路徑載入 + 使用者旅程',
    monitoringSource: 'Webapp Playwright + Caddy',
    runbook: '確認 user webapp host 與 admin host 分離，避免 path fallback 互相干擾。',
    failureSegment: 'deployment-infra',
  },
  {
    id: 'signaling',
    component: 'Signaling',
    status: 'partial',
    owner: '裝置 / 即時通訊',
    liveSignal: 'device / app WS、devlog SSE proxy',
    monitoringSource: 'Signaling 服務 logs + 外部 dashboard',
    route: '/devices',
    runbook: '先看 device detail/devlog，再查 WS session、mTLS/internal bridge 與 relay ratio。',
    failureSegment: 'deployment-infra',
  },
  {
    id: 'recording',
    component: 'Recording',
    status: 'external',
    owner: '錄影服務',
    liveSignal: 'NVR / 錄影任務健康',
    monitoringSource: 'Grafana / CloudWatch + signaling-recording logs',
    runbook: '檢查 ramdisk/fallback disk、upload queue、S3 failure 與 NVR device mapping。',
    failureSegment: 'deployment-infra',
  },
  {
    id: 'event-system',
    component: 'Event System',
    status: 'external',
    owner: '事件管線',
    liveSignal: '事件擷取、DLQ、consumer lag',
    monitoringSource: 'Grafana / CloudWatch + event-system logs',
    runbook: '先比對 audit/recent_events，再查 queue depth、DLQ 與 consumer lag。',
    failureSegment: 'backend-api',
  },
  {
    id: 'firmware-server',
    component: 'Firmware Server',
    status: 'partial',
    owner: '韌體平台',
    liveSignal: 'GET /v3/admin/fw/* proxy',
    monitoringSource: 'Admin 韌體頁 + firmware-server logs',
    route: '/firmware',
    runbook: '確認 proxy allowlist、model query、release state machine 與 FW-1/FW-2 缺口。',
    failureSegment: 'backend-api',
  },
  {
    id: 'cs-copilot',
    component: 'CS Copilot / Butler',
    status: 'partial',
    owner: 'AI 後端 / John',
    liveSignal: 'butler 經 REST internal bridge 取得 grounding',
    monitoringSource: 'cs-copilot logs + REST internal 探測',
    runbook: '確認 `/internal/*` mTLS 正式化前不要把 DEVONLY bridge 視為生產機制。',
    failureSegment: 'deployment-infra',
  },
  {
    id: 'storage-s3',
    component: 'Storage / S3',
    status: 'external',
    owner: '基礎設施',
    liveSignal: '來自 platform settings 的 export / docs / recording bucket 參照',
    monitoringSource: 'CloudWatch S3 指標 + platform settings 淨化後參照',
    route: '/platform-settings',
    runbook: '檢查 bucket refs、IAM policy、upload failure、retention 與 object lifecycle。',
    failureSegment: 'external-provider',
  },
  {
    id: 'message-email-sms',
    component: 'Email / SMS / notification',
    status: 'external',
    owner: '訊息供應商',
    liveSignal: '忘記密碼信件、未來 SMS 重設與告警',
    monitoringSource: 'provider dashboard + 淨化後 secret 狀態',
    route: '/platform-settings',
    runbook: '確認 sender domain、API key secret、provider delivery log、rate limit 與告警收件人設定。',
    failureSegment: 'external-provider',
  },
  {
    id: 'oauth-integrations',
    component: 'OAuth / 3P integrations',
    status: 'planned',
    owner: '平台整合',
    liveSignal: '未來平台對平台 OAuth2 端點',
    monitoringSource: 'endpoint 存在後的整合 logs',
    runbook: '先補 OAuth2/OIDC spec、client registration、scopes、audit，再做 UI。',
    failureSegment: 'spec-contract',
  },
  {
    id: 'secrets-kms',
    component: 'Secrets / KMS',
    status: 'external',
    owner: '基礎設施 / 安全',
    liveSignal: '僅淨化後的 secret 狀態',
    monitoringSource: 'Secrets Manager / KMS dashboard',
    route: '/platform-settings',
    runbook: 'secret value 不進瀏覽器；只檢查 configured/missing/drift 狀態與 rotation runbook。',
    failureSegment: 'external-provider',
  },
  {
    id: 'grafana-cloudwatch',
    component: 'Grafana / CloudWatch',
    status: 'external',
    owner: '可觀測性',
    liveSignal: '基礎設施指標、SLO、告警',
    monitoringSource: '外部監控 dashboard',
    runbook: 'raw metrics 與 alert rule 由外部監控維護，Admin 只列 ownership/readiness。',
    failureSegment: 'external-provider',
  },
];

export const FAILURE_SEGMENTS: FailureSegment[] = [
  {
    id: 'spec-contract',
    label: '規格 / 契約',
    owner: '產品 + 後端負責人',
    when: '畫面、測試與後端契約互相矛盾，或缺少資料口徑定義。',
    action: '先修 spec 或開決策 issue，再改 test/code。',
  },
  {
    id: 'testcase-fixture',
    label: '測試案例 / 夾具',
    owner: '測試負責人',
    when: '測試假設固定排序、固定數量、固定資料，或 mock shape 與活體 JSON 不符。',
    action: '修 fixture 與斷言，避免用位置或漂移資料作為真相。',
  },
  {
    id: 'frontend-code',
    label: '前端程式',
    owner: 'Admin 前端',
    when: '已確認 API/資料正確，但路由、狀態、權限、render 或交互錯。',
    action: '修 admin code，補 unit/E2E，確認 console/page errors 為 0。',
  },
  {
    id: 'backend-api',
    label: '後端 API',
    owner: 'REST / 服務負責人',
    when: '端點缺失、信封錯、RBAC 錯、request_id 缺、aggregation 尚未實作。',
    action: '回補 REST/service 端點或調整 catalog status，不在前端造假。',
  },
  {
    id: 'data-seed',
    label: '資料 / 種子資料',
    owner: 'Ops + 測試資料負責人',
    when: 'fixture 被刪、角色/資料域錯、dev fleet 漂移或缺少測試資料。',
    action: '修 seed/資料保留清單，再重跑測試。',
  },
  {
    id: 'deployment-infra',
    label: '部署 / 基礎設施',
    owner: '基礎設施 / 部署負責人',
    when: 'Caddy、DNS、CORS、env、secret ref、mTLS、service bridge 或外部 dashboard 掛掉。',
    action: '先修部署與環境，並在 MR note 標明不是 code regression。',
  },
  {
    id: 'external-provider',
    label: '外部供應商',
    owner: '供應商負責人',
    when: 'Email/SMS、S3、KMS、Grafana/CloudWatch、OAuth provider 等第三方或外部系統異常。',
    action: '查 provider dashboard/logs，Admin 只呈現 sanitized 狀態與 runbook。',
  },
];

export const PLATFORM_REFERENCE_LENSES: PlatformReferenceLens[] = [
  {
    id: 'app-account-home',
    domain: 'App / Account / Home',
    referenceSurface: 'App SDK / home graph：account、home/site、room、member、device/group、scene automation',
    lsCloudDecision: 'Users、終端帳號全貌已有；home/site/room/group/scene 只在 LS automation 需要支援查詢時補，不為 SDK parity 建 CRUD',
    status: 'planned',
    route: '/users',
    nextStep: '先確認 common/09 與 APP 是否需要 admin read model；不得把 developer org 或 B2B company 當 home/family。',
  },
  {
    id: 'device-fleet',
    domain: 'Device / Fleet',
    referenceSurface: '裝置管理、裝置紀錄、離線告警、IPC / 垂直能力',
    lsCloudDecision: 'Admin 可全平台看 device/app/debug 與 component 線索；Developer Portal 只看自己 org 的 device/app；控制與配網仍由 App/裝置端承載',
    status: 'live',
    route: '/devices',
    nextStep: '把 recording/NVR/backup 狀態納入裝置詳情，不直接做 WSS wire 代操。',
  },
  {
    id: 'product-firmware',
    domain: 'Product / Firmware',
    referenceSurface: '產品開發、機型審核、韌體歸屬、canary OTA、批量 OTA',
    lsCloudDecision: 'Developer Review 與 Firmware lifecycle 已有；binary upload、manifest publish、ota_jobs 細進度仍缺',
    status: 'blocked',
    route: '/firmware',
    nextStep: '等待 FW-1/FW-2 後補 firmware P2，不做假上傳 UI。',
  },
  {
    id: 'cloud-integration',
    domain: 'Cloud / OpenAPI',
    referenceSurface: 'OpenAPI、API Explorer、訊息訂閱、授權、專案連結',
    lsCloudDecision: 'API Keys、Platform Settings 已有；event channel admin 僅在 LS event-system 需要時補；拒絕 Cloud Project linking 模型',
    status: 'planned',
    route: '/api-keys',
    nextStep: '先定義 event-system/admin message-channel API、owner、retention 與 alert policy，再補 UI。',
  },
  {
    id: 'data-observability',
    domain: 'Data / Observability',
    referenceSurface: 'Data Dashboard、Data Analytics、監控紀錄、裝置訊息統計',
    lsCloudDecision: 'Dashboard KPI、Reports catalog、Monitoring readiness、Audit、devlog 已有；raw metrics/alert rules 仍走 Grafana/CloudWatch',
    status: 'partial',
    route: '/reports',
    nextStep: '只補 LS reporting aggregation API；不做外部平台式任意 BI/Data Visualization clone。',
  },
  {
    id: 'governance-trust',
    domain: 'Governance / Trust',
    referenceSurface: 'IAM、韌體歸屬、資產授權、安全 / 合規',
    lsCloudDecision: 'Roles、PKI、Compliance、Audit 已有；customer→brand/family lifecycle 依 LS customer/family model，不導入外部 asset/project 概念',
    status: 'planned',
    route: '/operations',
    nextStep: '優先補 Customer/Brand(Family) 管理規格與後端端點。',
  },
];

export const DASHBOARD_WORKFLOWS: DashboardWorkflow[] = [
  {
    title: 'Customer support',
    scope: '使用者查詢、終端帳號全貌、帳號處置',
    control: 'case_id / reason',
    tone: 'pill-info',
    to: '/users',
  },
  {
    title: 'Fleet operations',
    scope: '裝置狀態、devlog、停用 / 掛失',
    control: 'audit guarded',
    tone: 'pill-info',
    to: '/devices',
  },
  {
    title: 'Organizations',
    scope: 'Developer org、owner、members、pending invites',
    control: 'live endpoint',
    tone: 'pill-good',
    to: '/organizations',
  },
  {
    title: 'B2B Companies',
    scope: '公司資料、出貨批次、invoice',
    control: 'billing surface',
    tone: 'pill-info',
    to: '/companies',
  },
  {
    title: 'Developer Review',
    scope: '開發者申請與機型發布審核',
    control: 'reject reason',
    tone: 'pill-warn',
    to: '/dev-reviews',
  },
  {
    title: 'Customer Families',
    scope: 'customer org、brand family、client app seed',
    control: 'reason / case_id',
    tone: 'pill-info',
    to: '/customer-families',
  },
  {
    title: 'Platform access',
    scope: 'API keys、RBAC roles、platform settings',
    control: 'least privilege',
    tone: 'pill-neutral',
    to: '/api-keys',
  },
  {
    title: 'Reports',
    scope: 'KPI、support、fleet、firmware、security、billing 報表 catalog',
    control: 'live vs gap',
    tone: 'pill-info',
    to: '/reports',
  },
  {
    title: 'Platform monitoring',
    scope: 'REST、admin、portal、signaling、recording、event、storage component readiness',
    control: 'failure segment',
    tone: 'pill-warn',
    to: '/monitoring',
  },
  {
    title: 'Platform reference review',
    scope: 'App/Home/Device/Product/Cloud/Data/Governance 取捨',
    control: 'gap matrix',
    tone: 'pill-neutral',
    to: '/operations',
  },
];

// C11:⌘K 全域搜尋宣告(§0.3(h) 單一 manifest)——導覽目標取 ADMIN_NAV_GROUPS,
// 實體型別/路由模板/顯示名在此宣告;AppShell 與 palette 不得硬編碼搜尋範圍。
// 後端契約 = GET /v3/admin/search(restful 02 §3.3.2,restful!150)。
export const SEARCH_ENTITY_ROUTES: Record<string, (id: string) => string> = {
  user: (id) => `/users/${encodeURIComponent(id)}`,
  device: (id) => `/devices/${encodeURIComponent(id)}`,
  org: () => '/organizations',
};

export const SEARCH_ENTITY_LABEL: Record<string, string> = {
  user: '使用者',
  device: '裝置',
  org: '開發者組織',
};

export function allNavItems(): AdminNavItem[] {
  return ADMIN_NAV_GROUPS.flatMap((group) => group.items);
}

export function allMechanisms(): AdminMechanism[] {
  return [...LIVE, ...ROADMAP, ...EXTERNAL];
}
